/** Ejecutor común de acciones de flujo del servidor (design.md §22.7).
 *
 * Recibe todas las acciones del contexto, muestra las deshabilitadas con el
 * motivo del servidor, pide el motivo solo cuando `requires_reason`, captura
 * los `data_fields` y propaga la revisión del documento. Un 409 exige recarga
 * explícita; no hay reintento automático.
 */
import { useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { MobileButton } from './MobileButton';
import { MobileIcon, type MobileIconName } from './MobileIcon';
import { extractRevisionConflict } from '@/services/mobileApiClient';
import type { WorkflowContextAction } from '@/services/tasksService';
import {
  isWorkflowActionDisabled,
  workflowActionCode,
  workflowActionDisabledReason,
  type WorkflowActionPayload,
} from '@/utils/workflowActions';

export const isServerActionDisabled = isWorkflowActionDisabled;
export const serverActionDisabledReason = workflowActionDisabledReason;

type MobileButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning';

const ACTION_ICONS: Record<string, MobileIconName> = {
  rule: 'fact_check',
  feedback: 'edit_note',
  undo: 'arrow_back',
  send: 'send',
  request_quote: 'request_quote',
  design_services: 'design_services',
  check_circle: 'check_circle',
  cancel: 'cancel',
  block: 'block',
  approval: 'how_to_reg',
  task_alt: 'task_alt',
  content_copy: 'add_circle',
  schedule: 'schedule',
  thumb_up: 'check_circle',
  thumb_down: 'cancel',
  payments: 'payments',
  receipt_long: 'receipt_long',
};

function actionIcon(action: WorkflowContextAction): MobileIconName {
  const code = workflowActionCode(action);
  if (action.icon && ACTION_ICONS[action.icon]) return ACTION_ICONS[action.icon];
  if (code === 'cancel') return 'cancel';
  if (code.includes('withdraw') || code.includes('resume')) return 'refresh';
  if (code.includes('version')) return 'add_circle';
  return 'send';
}

function actionVariant(action: WorkflowContextAction): MobileButtonVariant {
  if (action.variant === 'success') return 'success';
  if (action.variant === 'danger') return 'danger';
  if (action.variant === 'warning') return 'warning';
  if (action.variant === 'primary') return 'primary';
  return workflowActionCode(action) === 'cancel' ? 'danger' : 'primary';
}

type ServerActionRunnerProps = {
  actions: WorkflowContextAction[];
  /** Revisión del documento/contexto para X-Expected-Document-Revision. */
  documentRevision?: number;
  /** Texto del motivo pedido cuando la acción lo exige. */
  reasonLabel?: string;
  onExecute: (
    actionCode: string,
    body: WorkflowActionPayload,
    revision?: number,
  ) => Promise<unknown>;
  onSuccess?: (actionCode: string, result: unknown) => void;
  /** Recarga explícita tras un 409 (el runner no conoce el refetch). */
  onConflictReload?: () => void;
};

export function ServerActionRunner({
  actions,
  documentRevision,
  reasonLabel = 'Motivo',
  onExecute,
  onSuccess,
  onConflictReload,
}: ServerActionRunnerProps) {
  const [pending, setPending] = useState<WorkflowContextAction | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  if (actions.length === 0) return null;

  function resetDialog() {
    setPending(null);
    setReason('');
    setReasonError(null);
    setFieldValues({});
    setFieldErrors({});
    setFailed(null);
    setConflict(false);
  }

  function openAction(action: WorkflowContextAction) {
    setPending(action);
    setReason('');
    setReasonError(null);
    setFieldValues(
      Object.fromEntries((action.data_fields ?? []).map((field) => [field.name, ''])),
    );
    setFieldErrors({});
    setFailed(null);
    setConflict(false);
  }

  function closeDialog() {
    if (busy) return;
    resetDialog();
  }

  function updateField(name: string, value: string) {
    setFieldValues((current) => ({ ...current, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  }

  async function confirmAction() {
    if (!pending || busy) return;
    const code = workflowActionCode(pending);
    if (pending.requires_reason && reason.trim().length === 0) {
      setReasonError('El motivo es obligatorio');
      return;
    }
    const missing = (pending.data_fields ?? []).find(
      (field) => field.required && (fieldValues[field.name] ?? '').trim() === '',
    );
    if (missing) {
      setFieldErrors({ [missing.name]: `Completa ${missing.label}` });
      return;
    }
    setReasonError(null);
    setFieldErrors({});
    setFailed(null);
    setConflict(false);
    setBusy(true);
    try {
      const body: WorkflowActionPayload = {};
      if (pending.requires_reason) body.reason = reason.trim();
      for (const field of pending.data_fields ?? []) {
        const value = (fieldValues[field.name] ?? '').trim();
        if (value !== '') body[field.name] = value;
      }
      const result = await onExecute(code, body, documentRevision);
      onSuccess?.(code, result);
      setPending(null);
      setReason('');
      setFieldValues({});
    } catch (error) {
      if (extractRevisionConflict(error)) {
        setConflict(true);
        setFailed('Los datos cambiaron en el servidor. Recarga para ver el estado actual.');
      } else {
        setFailed(error instanceof Error ? error.message : 'No se pudo ejecutar la acción');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      {actions.map((action) => {
        const code = workflowActionCode(action);
        const disabled = isWorkflowActionDisabled(action);
        const disabledReason = workflowActionDisabledReason(action);
        return (
          <div key={code}>
            <MobileButton
              variant={actionVariant(action)}
              size="md"
              fullWidth
              disabled={disabled}
              icon={<MobileIcon name={actionIcon(action)} className="h-5 w-5" />}
              onClick={() => openAction(action)}
            >
              {action.label}
            </MobileButton>
            {disabled && disabledReason && (
              <p className="mt-1 break-words px-1 text-xs text-text-tertiary">{disabledReason}</p>
            )}
          </div>
        );
      })}

      {pending && !conflict && (
        <ConfirmDialog
          title={pending.label}
          message={
            pending.requires_reason
              ? 'Esta acción registra el motivo en la auditoría.'
              : `¿Confirmas "${pending.label}"?`
          }
          confirmLabel="Confirmar"
          danger={workflowActionCode(pending) === 'cancel' || pending.variant === 'danger'}
          busy={busy}
          error={failed}
          reason={
            pending.requires_reason
              ? {
                  label: reasonLabel,
                  value: reason,
                  onChange: (next: string) => {
                    setReason(next);
                    if (reasonError && next.trim().length > 0) setReasonError(null);
                  },
                  error: reasonError ?? undefined,
                }
              : undefined
          }
          fields={(pending.data_fields ?? []).map((field) => ({
            name: field.name,
            label: field.label,
            required: field.required,
            value: fieldValues[field.name] ?? '',
            options: field.options,
            onChange: (next: string) => updateField(field.name, next),
            error: fieldErrors[field.name],
          }))}
          onConfirm={() => void confirmAction()}
          onCancel={closeDialog}
        />
      )}

      {pending && conflict && (
        <ConfirmDialog
          title="Datos desactualizados"
          message={failed ?? 'Los datos cambiaron en el servidor.'}
          confirmLabel="Recargar"
          busy={busy}
          onConfirm={() => {
            resetDialog();
            onConflictReload?.();
          }}
          onCancel={closeDialog}
        />
      )}
    </div>
  );
}
